import { type IApiManager, type IEventEmitter } from '@source-digital/sd-app-core'
import { LocalClientApiManager, LocalEventEmitter } from './local'
import { type IBrokerMessenger } from './base'
import { InternalBrokerMessenger } from './internal'

/**
 * aggregates multiple messengers.
 */
class RootMessenger implements IBrokerMessenger {
  messengers: { internal: InternalBrokerMessenger } & Record<string, IBrokerMessenger> = {
    internal: new InternalBrokerMessenger()
  }

  private getTargetMessengers (): IBrokerMessenger[] {
    return Object.values(this.messengers)
  }

  addMessenger (name: string, messenger: IBrokerMessenger) {
    this.messengers[name] = messenger
    // copy existing events
    for (const [event, receiverMap] of this.messengers.internal.listeners.entries()) {
      for (const [receiverId, handlers] of receiverMap.entries()) {
        for (const handler of handlers) {
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          messenger.on(event, handler, receiverId)
        }
      }
    }
  }

  createLocalEmitter (id: string): IEventEmitter {
    return new LocalEventEmitter(id, this)
  }

  createLocalClientApiManager (id: string): IApiManager {
    return new LocalClientApiManager(id, this)
  }

  emit (...args: Parameters<IBrokerMessenger['emit']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.emit(...args) })
  }

  on (...args: Parameters<IBrokerMessenger['on']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.on(...args) })
  }

  once (...args: Parameters<IBrokerMessenger['once']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.once(...args) })
  }

  off (...args: Parameters<IBrokerMessenger['off']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.off(...args) })
  }

  registerApi (...args: Parameters<IBrokerMessenger['registerApi']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.registerApi(...args) })
  }

  unregisterApi (...args: Parameters<IBrokerMessenger['unregisterApi']>): void {
    this.getTargetMessengers().forEach(messenger => { messenger.unregisterApi(...args) })
  }

  getApi<T> (apiName: string): T {
    return this.messengers.internal.getApi<T>(apiName)
  }

  hasClient (...args: Parameters<IBrokerMessenger['hasClient']>): boolean {
    return this.getTargetMessengers().some(messenger => messenger.hasClient(...args))
  }

  async connectClientApi<T = any> (targetId: string, apiName: string): Promise<T> {
    for (const messenger of this.getTargetMessengers()) {
      if (messenger.hasClient(targetId)) {
        return await messenger.connectClientApi(targetId, apiName)
      }
    }
    return await Promise.reject(new Error(`api ${apiName} of client ${targetId} not found`))
  }

  registerClientApi (targetId: string, apiName: string, apiDefinition: unknown): void {
    this.messengers.internal.registerClientApi(targetId, apiName, apiDefinition)
  }

  unregisterClientApi (targetId: string, apiName: string): void {
    this.messengers.internal.unregisterClientApi(targetId, apiName)
  }
}

export { RootMessenger }
